<template>
  <component
    :is="tag"
    class="n-list-item"
    ref="listItem"
    :aria-disabled="disabled ? 'true' : undefined"
    v-on="itemListeners"
  >
    <div class="list-item" :class="{ active, disabled }">
      <div class="leading-section">
        <div v-if="hasRadioButton" class="radio">
          <n-radio :value="value" :selection="selection" @selected="$emit('selected', $event)" />
        </div>

        <div :class="['icon', leadingIconColor]" v-if="hasLeadingIcon">
          <n-icon :icon="leadingIcon" :rotate="leadingIconRotate" size="s" />
        </div>
      </div>

      <div class="text" v-if="hasText">
        <slot>{{ text }}</slot>
      </div>
      <div v-if="hasTrailingIcon">
        <n-icon :icon="trailingIcon" size="s" />
      </div>

      <slot name="action">
        <n-icon v-if="checked" icon="check" size="s" class="submenu-chevron" />
        <n-icon v-if="$slots.submenu" icon="chevron-right" size="s" class="submenu-chevron" />
        <div v-if="$slots.submenu" class="submenu-wrapper" :class="{ 'open-left': submenuOpenLeft }">
          <slot v-if="showSubmenuInternal" name="submenu" />
        </div>
      </slot>
    </div>
  </component>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import UserAvatar from '@/modules/global/components/User/UserAvatar.vue'

@Component({
  components: { UserAvatar }
})
export default class NListItem extends Vue {
  @Prop({ type: String }) text!: string
  @Prop({ default: 'div' }) tag!: 'div' | 'li' | 'span'
  @Prop({ type: String }) leadingIcon?: string
  @Prop({ type: String, default: '0' }) leadingIconRotate!: string
  @Prop({ type: String }) trailingIcon?: string
  @Prop({ default: 'black' }) leadingIconColor?: 'black' | 'sand'
  @Prop({ default: false }) checked?: boolean
  @Prop() active!: boolean
  @Prop() selection!: any
  @Prop() value!: any
  @Prop({ type: Boolean }) showSubmenu?: boolean
  @Prop({ type: Boolean, default: false }) disabled!: boolean

  @Ref('listItem') listItemRef!: HTMLElement

  submenuOpenLeft = false
  showSubmenuInternal = false

  /** A disabled row keeps its parent's other listeners but never reports a click */
  get itemListeners() {
    if (!this.disabled) return this.$listeners
    const listeners = { ...this.$listeners }
    delete listeners.click
    return listeners
  }

  get hasRadioButton(): boolean {
    return !!this.value
  }

  get hasAvatar(): boolean {
    return this.$slots.avatar !== undefined
  }

  get hasText(): boolean {
    return this.$slots.default !== undefined || this.text !== undefined
  }

  get hasLeadingIcon(): boolean {
    return !!this.leadingIcon
  }

  get hasTrailingIcon(): boolean {
    return !!this.trailingIcon
  }

  @Watch('showSubmenu') showSubmenuChanged() {
    if (this.showSubmenu) {
      setTimeout(() => {
        this.showSubmenuInternal = true
        this.$nextTick(() => {
          const menus = document.getElementsByClassName('menu-wrapper')
          const listItemRect = this.listItemRef.getBoundingClientRect()
          // If already opend to the left, open further to the left
          for (let i = 0; i <= menus.length; i++) {
            if (!!menus.item(i) && !this.$el.contains(menus.item(i))) {
              const menuRect = menus.item(i)!.getBoundingClientRect()
              if (menuRect.left > listItemRect.left) {
                this.submenuOpenLeft = true
                return
              }
            }
          }

          const offset = 20
          const menuRightPosWithSubmenu = listItemRect.right + listItemRect.width + offset

          // Check if menu would be off screen
          if (menuRightPosWithSubmenu > document.body.clientWidth) {
            this.submenuOpenLeft = true
          } else {
            this.submenuOpenLeft = false
          }
        })
      }, 25)
    } else {
      this.showSubmenuInternal = false
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.n-list-item {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.list-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: $notch-space-2x $notch-space-3x;
  width: 100%;
  color: $notch-color-neutral-grey-900;
  height: $notch-space-9x;
  border-radius: $notch-radius-10x;

  cursor: pointer;
  background: $notch-color-neutral-grey-000;

  &.active {
    background: $notch-color-neutral-sand-100;
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background: $notch-color-neutral-grey-000;
    }
  }

  &:hover {
    background: $notch-color-neutral-sand-100;
  }

  &:focus {
    @include notch-style-focus;
  }

  .text {
    @include notch-text-label-light-M;
    flex-grow: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .avatar {
    margin-right: $notch-space-2x;
  }

  .action {
    display: flex;
  }

  .leading-section {
    display: inline-flex;
    align-items: center;

    .icon,
    .avatar,
    .radio {
      margin-right: $notch-space-2x;
    }

    .icon.sand {
      color: $notch-color-neutral-sand-400;
    }
    .icon.black {
      color: $notch-color-neutral-grey-900;
    }
  }

  > .submenu-chevron {
    margin-left: auto;
  }
}

.submenu-wrapper {
  position: absolute;
  left: 100%;
  top: -$notch-space-2x;
  margin-left: $notch-space-2x;

  &.open-left {
    left: unset;
    margin-left: unset;
    right: 100%;
    z-index: $notch-layer-1;
    margin-right: $notch-space-2x;
  }
}
</style>
